/**
 * Tiny URL helper for per-game session ids.
 *
 * Every game gets a URL of the form `{base}/g/<id>` (`/g/<id>` in Capacitor / local Vite,
 * `/rithmomachia/g/<id>` on the aethongames.com deploy). The id is a client-generated
 * 32-hex string minted at newGame time — every game across every mode (local / bot / online,
 * signed in or not) updates the URL immediately, so the URL is the visible "this is a game
 * in progress" signal and survives reload.
 *
 * Signed-in bot/online games ALSO get a server-side gameRecordId in D1 for the recording
 * API. The two ids are independent today; a later deep-link-replay feature will need to
 * either store the client sessionId in D1 alongside the server id, or unify them.
 */

/** Vite `base` without a trailing slash. Empty string when the app is served at `/`. */
export function appBase(): string {
  const raw = (import.meta.env.BASE_URL as string | undefined) ?? "/";
  return raw.replace(/\/+$/, "");
}

/** Menu URL for this build (`/` or `/rithmomachia/`). */
export function appHome(): string {
  const base = appBase();
  return base === "" ? "/" : `${base}/`;
}

/** Resolve a file in Vite's `public/` against the current base. */
export function publicUrl(file: string): string {
  const base = (import.meta.env.BASE_URL as string | undefined) ?? "/";
  const prefix = base.endsWith("/") ? base : `${base}/`;
  return `${prefix}${file.replace(/^\//, "")}`;
}

export function newSessionId(): string {
  const arr = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function setGameUrl(gameId: string): void {
  if (typeof window === "undefined" || !window.history) return;
  const target = `${appBase()}/g/${gameId}`;
  if (window.location.pathname === target) return;
  try {
    window.history.replaceState({ gameId }, "", target);
  } catch {
    // pushState can fail in sandboxed contexts (iframes, file://); URL is non-essential here.
  }
}

export function clearGameUrl(): void {
  if (typeof window === "undefined" || !window.history) return;
  const home = appHome();
  const path = window.location.pathname;
  if (path === home || path === appBase() || path === "/") return;
  try {
    window.history.replaceState({}, "", home);
  } catch {
    /* noop */
  }
}

/**
 * Read a game id from the current URL, if any. Returns null when the path isn't `{base}/g/<id>`.
 * Validates that the id looks like the 32-hex-char shape we mint server-side (so a stray
 * `/g/anything` doesn't get treated as a real id). Also accepts a root `/g/<id>` so a
 * same-origin redirect from the old URLs still restores after the history update.
 */
export function gameIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const base = appBase();
  const path = window.location.pathname;
  const prefixed = base ? new RegExp(`^${base}/g/([a-f0-9]{32})/?$`, "i") : null;
  const m = (prefixed && path.match(prefixed)) || path.match(/^\/g\/([a-f0-9]{32})\/?$/i);
  return m ? m[1].toLowerCase() : null;
}
